import I18n from 'I18n';
import { useRef, useState } from 'react';
import { LayoutType } from 'signup-components/types';
import { Step } from 'signup-constants/Step';
import { useSignupEmbedder } from 'signup-embedder/hooks/useSignupEmbedder';
import { useButtonGridFocus } from '../hooks/useButtonGridFocus';
import { useButtonGridPreferredColumnCount } from '../hooks/useButtonGridPreferredColumnCount';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { useIsFramedCentredStep } from '../hooks/useIsFramedCentredStep';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { useResponsiveness } from '../responsiveness/ResponsivenessProvider';
import { getFlowKey } from '../routing/getFlowKey';
import { isTransitionAndAnimationsDisabled } from '../selenium/seleniumFeatures';
import { useSignupData } from '../signup-data/useSignupData';
import { ButtonsGridButton, ButtonsGridButtonHolder, ButtonsGridStyled, HubletIcon, StyledButtonText, getButtonColumnCount } from '../styles/ButtonsGrid.styles';
import { useView } from '../views/useView';
import NextButtonWrapper from './NextButtonWrapper';
import { isUseCaseButtonDisabled } from '../views/UseCaseView';
import { useTracker } from '../tracking/useTracker';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { useExperiment, TrackWhen } from '../experiments/useExperiment';
import { HubletToImageUrl } from '../hublets/hubletSelectionUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const defaultGetButtonText = ({
  dataKey,
  key
}) => {
  return I18n.text(`views.${dataKey}.options.${key}`);
};
export const ButtonsGrid = ({
  columnCount,
  optionHeight,
  options,
  dataKey,
  stepKey,
  type,
  hasNextButton = false,
  nextButtonLabel,
  shouldDisableAutofocus = false,
  getButtonDescriptionText,
  getButtonText = defaultGetButtonText,
  onSelect = () => {},
  onHighlightChange = () => {},
  setDataFn,
  isSelected = () => false,
  shouldCenter = true,
  goToNextStep = true
}) => {
  const gridRef = useRef(null);
  const [clickedNextButton, setClickedNextButon] = useState(false);
  const flowKey = getFlowKey();
  const {
    submit
  } = useView({
    stepKey
  });
  const {
    data,
    setData
  } = useSignupData(dataKey);
  const {
    preferredColumnCount
  } = useButtonGridPreferredColumnCount(options, dataKey, getButtonText);
  const {
    isAssignedToVariant: isACQ0149Variant
  } = useExperiment(ExperimentKeys.ACQ0149, TrackWhen.never);
  const {
    currentStep
  } = useCurrentStep();
  const isHubletSelectionStep = currentStep === Step.HubletSelection;
  const {
    isEmbedded
  } = useSignupEmbedder();
  const {
    isMobile
  } = useResponsiveness();
  const {
    layoutType
  } = useLayoutTemplate();
  const Tracker = useTracker();
  const isFramedCentredStepLayout = layoutType === LayoutType.FramedCentredStep;
  const actualColumnCount = columnCount || preferredColumnCount || getButtonColumnCount(currentStep, isMobile, isEmbedded, isFramedCentredStepLayout);
  const {
    isFramedCentredStep
  } = useIsFramedCentredStep();
  const onSelectedChange = value => {
    setClickedNextButon(true);
    onSelect(value);
    submit();
  };
  useButtonGridFocus(gridRef, options.length, shouldDisableAutofocus);
  const onClickOption = (key, response) => {
    if (setDataFn) {
      setDataFn(key, setData);
    } else {
      setData(key);
    }
    if (!hasNextButton && goToNextStep) {
      onSelectedChange(key);
    } else {
      onHighlightChange(key);
      if (response && response.target.value) {
        Tracker.signupInteraction(`highlight-${dataKey}`, {
          selectedOption: [key]
        });
      } else {
        Tracker.signupInteraction(`deselect-${dataKey}`, {
          selectedOption: [key]
        });
      }
    }
  };
  const nthChild = actualColumnCount % 2 === 0 ? 'odd' : 'even';
  const buttonHeight = optionHeight;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ButtonsGridStyled
    // @ts-expect-error Expression produces a union type that is too complex to represent.
    , {
      className: "buttonsGrid",
      columnCount: columnCount,
      currentStep: currentStep,
      flowKey: flowKey,
      isFramedCentredStep: isFramedCentredStep,
      isFramedCentredStepLayout: isFramedCentredStepLayout,
      layoutType: layoutType,
      nthChild: nthChild,
      offset: actualColumnCount - 1,
      ref: gridRef,
      shouldCenter: shouldCenter && options.length % actualColumnCount !== 0,
      children: options.map((key, index) => /*#__PURE__*/_jsx(ButtonsGridButtonHolder, {
        minHeight: buttonHeight,
        children: /*#__PURE__*/_jsx(ButtonsGridButton, Object.assign({
          block: true,
          className: "ButtonsGrid_Button",
          currentStep: currentStep,
          "data-selenium": `${dataKey}-${index}`,
          "data-selenium-option": key,
          description: getButtonDescriptionText && getButtonDescriptionText({
            dataKey,
            key,
            index
          }),
          disable: isTransitionAndAnimationsDisabled(),
          disabled: currentStep === Step.UseCase ? isUseCaseButtonDisabled(data, key) : false,
          flowKey: flowKey,
          isFramedCentredStep: isFramedCentredStep,
          name: 'selectable-button',
          selected: data === key || isSelected(key),
          title: /*#__PURE__*/_jsx(StyledButtonText, {
            className: "m-all-0",
            isACQ0149Variant: isACQ0149Variant(),
            isHubletButtonText: isHubletSelectionStep,
            type: type,
            children: getButtonText({
              dataKey,
              key,
              index
            })
          }),
          truncatable: false,
          type: type
        }, type === 'radio' ? {
          onClick: () => {
            onClickOption(key);
          }
        } : {
          onSelectedChange: response => {
            onClickOption(key, response);
          }
        }, {
          children: isHubletSelectionStep && /*#__PURE__*/_jsx(HubletIcon, {
            responsive: true,
            src: HubletToImageUrl[key]
          })
        }))
      }, key))
    }), hasNextButton && /*#__PURE__*/_jsx(NextButtonWrapper, {
      label: nextButtonLabel,
      loading: clickedNextButton,
      onClick: () => onSelectedChange(data)
    })]
  });
};